import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from 'react-bootstrap'
import { ARB_TASK_MAP_ID, MAP_RENDER_API_KEY } from '../utils/shared/constants'
import { Loader } from "@googlemaps/js-api-loader"
import { generateMapPin } from '../utils/presentation'
import { Place, PlaceGeocoding } from '../models/shared/Place'
import { useText } from '../context/TextContext'
import { geocodingToPlace } from '../utils/geolocation'

interface Props {
    placeGeocoding?: PlaceGeocoding
    onPlaceChange: (place: Place) => void
}


const MAP_ELEMENT_ID = 'map-form';
export default function MapPublicForm(props: Props) {
    const [selectedLocation, setSelectedLocation] = useState<PlaceGeocoding | null>(null);
    const infoWindowRef = useRef<any>(null);
    const infoWindowContentRef = useRef<HTMLDivElement | null>(null);
    const text = useText();

    useEffect(() => {
        const geocoding = props.placeGeocoding;
        if (!geocoding) {
            return;
        }

        const asyncEffect = async () => {
            const loader = new Loader({ apiKey: MAP_RENDER_API_KEY });

            const mapElement = document.getElementById(MAP_ELEMENT_ID);
            if (!mapElement) {
                return;
            }

            try {
                const { Map, InfoWindow } = await loader.importLibrary('maps');
                const { AdvancedMarkerElement, PinElement } = await loader.importLibrary('marker');
                const map = new Map(mapElement,
                    {
                        // no controls, no fullscreen, no zoom
                        disableDefaultUI: true,
                        disableDoubleClickZoom: true,
                        center: geocoding,
                        zoom: 18,
                        mapId: ARB_TASK_MAP_ID,
                        clickableIcons: false,
                    });

                map.addListener('click', (event: any) => {
                    const location = {
                        lat: event.latLng.lat(),
                        lng: event.latLng.lng()
                    };

                    // Close previous info window if exists
                    if (infoWindowRef.current) {
                        infoWindowRef.current.close();
                    }

                    // Create container for React content
                    const contentDiv = document.createElement('div');
                    infoWindowContentRef.current = contentDiv;

                    const overlay = new InfoWindow({
                        content: contentDiv,
                        position: location,
                        headerDisabled: true,
                    })

                    infoWindowRef.current = overlay;
                    overlay.open({ map });

                    setSelectedLocation(location);
                });

                const faPin = generateMapPin(PinElement);

                new AdvancedMarkerElement({
                    position: geocoding,
                    map: map,
                    content: faPin.element
                });

            } catch (error) {
                console.error('Error loading map', error);
            }
        }

        asyncEffect();
    }, [props.placeGeocoding, props.onPlaceChange])

    const handleConfirmLocation = () => {
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
        if (selectedLocation) {
            props.onPlaceChange(geocodingToPlace(selectedLocation));
        }
        infoWindowRef.current = null;
        infoWindowContentRef.current = null;
        setSelectedLocation(null);
    };

    const handleCancelLocation = () => {
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
        infoWindowRef.current = null;
        infoWindowContentRef.current = null;
        setSelectedLocation(null);
    };

    // React component to render inside InfoWindow
    const LocationConfirmContent = () => (
        <div>
            <p>{text.useThisLocation}</p>
            <div className='d-flex justify-content-end gap-2'>
                <Button size='sm' onClick={handleCancelLocation} variant='secondary'>
                    {text.cancel}
                </Button>
                <Button size='sm' onClick={handleConfirmLocation}>
                    {text.confirm}
                </Button>
            </div>
        </div>
    );

    if (!props.placeGeocoding) {
        return <></>
    }

    return (
        <div className="position-relative w-100">
            <div id={MAP_ELEMENT_ID} className='border rounded' style={{ aspectRatio: '4/1' }} />
            {selectedLocation && infoWindowContentRef.current &&
                createPortal(<LocationConfirmContent />, infoWindowContentRef.current)
            }
        </div>
    );
}
